import React, { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'
import { ConfirmationModal, Table } from 'components/Ui'
import { deleteApiRecord } from 'services/CompanyConfigurationService'
import { Success } from 'utils/alerts'
import useAuth from 'utils/hooks/useAuth'
import { Title } from 'components/helper'
import dayjs from 'dayjs'
injectReducer('companyConfiguration', reducer)

export default function CompanyConfiguration() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const { data, tableData, loading } = useSelector(
        (state) => state.companyConfiguration.data,
    )

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.checkBox,
    ])

    const fetchData = () => dispatch(getData({ ...tableData }))

    const handleDelete = (id) => {
        deleteApiRecord(id)
            .then(() => {
                Success('Deleted Successfully!')
                fetchData()
            })
            .catch((error) => error)
    }

    const columns = useMemo(
        () => [
            {
                field: 'company',
                header: 'Company',
                className: 'min-w-[150px]',
                renderCell: (row) => (
                    <Title
                        title={row?.company?.name}
                        image={row?.company?.company_logo}
                    />
                ),
            },
            {
                field: 'free_users',
                header: 'Free Users',
                className: 'min-w-[150px]',
            },
            {
                field: 'monthly_limit',
                header: 'Monthly Limit',
                className: 'min-w-[150px]',
            },
            {
                field: 'monthly_limit_per_user',
                header: 'Monthly Limit Per User',
                className: 'min-w-[150px]',
            },
            {
                field: 'start_date',
                header: 'Start Date',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.start_date
                        ? dayjs(row?.start_date).format('DD-MMM-YYYY')
                        : '',
            },
            {
                field: 'end_date',
                header: 'Start Date',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.end_date
                        ? dayjs(row?.end_date).format('DD-MMM-YYYY')
                        : '',
            },
            {
                header: 'Action',
                field: 'action',
                className: 'px-4',
                renderCell: (row) => {
                    return (
                        <div className="flex items-center gap-3">
                            {checkPermission(
                                'company-configuration',
                                'edit',
                            ) ? (
                                <Tooltip title={'Edit'}>
                                    <Link
                                        to={`/company-management/company-configuration/${row.id}/edit`}
                                        className="hover:text-primary"
                                    >
                                        <HiOutlinePencil size={20} />
                                    </Link>
                                </Tooltip>
                            ) : null}
                            {checkPermission(
                                'company-configuration',
                                'delete',
                            ) ? (
                                <Tooltip title={'Delete'}>
                                    <ConfirmationModal
                                        message={
                                            'Are you sure you want to delete this record!'
                                        }
                                        onConfirm={() => {
                                            handleDelete(row.id)
                                        }}
                                    >
                                        <span className="hover:text-danger">
                                            <HiOutlineTrash size={20} />
                                        </span>
                                    </ConfirmationModal>
                                </Tooltip>
                            ) : null}
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [
            tableData.sort,
            tableData.query,
            tableData.pageIndex,
            tableData.pageSize,
            tableData.selectedTab,
            tableData.checkBox,
        ],
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">
                    Company Configuration
                </h2>
            </div>
            <Table
                loading={loading}
                filter={false}
                datas={data}
                columns={columns}
                add_new={
                    checkPermission('company-configuration', 'create')
                        ? '/company-management/company-configuration/create'
                        : ''
                }
                pagingData={tableData}
                onChange={(e) => dispatch(setTableData(e))}
            />
        </>
    )
}
